/*
 * Black Weeks: the 2in1 set's cover (Wechselbezug) taken as a voucher; fabric and colour are chosen later.
 *
 * The button sits next to "Auswählen" in the cover's variant modal (blocks/_kc-upsell-modal-sticky-footer.liquid).
 * The voucher variant follows the sofa's current price (data-bf-sofa-price on the set's [data-bf-config]):
 * below the first limit the first variant, from there to below the second the second, from the last limit on
 * the last one. The limit belongs to the UPPER tier (2500 -> the 600 variant, Ivan 19/10), as in the configurator
 * (offer2026.ts voucherAmountFor) and the checkout's cover-voucher-validation, which refuses any other tier.
 *
 * Choosing it does what saving a colour does (upsell-variant-selector.js): the voucher variant goes into the
 * cover's slot inputs, the cover checkbox is ticked and `upsell:variant-changed` repaints the set row, the totals
 * and the sticky bar (kc-bf-offer.js, product-detail-sticky.js). The voucher's product type is "Sofabezug", so the
 * gift rules count it as the cover. Saving a colour afterwards puts the cover back.
 *
 * In the cart the line carries `_cover_voucher`, which turns off "Bearbeiten" (snippets/kc-cart-line.liquid),
 * so the voucher's value cannot be switched there.
 */

const PROPERTY = "_cover_voucher";
const ACTIVE = "data-kc-voucher-variant-id";

const configFor = (coverId) =>
  document.querySelector(`[data-bf-config][data-bf-cover-product-id="${coverId}"]`);

const buttons = () => document.querySelectorAll("[data-kc-cover-voucher]");

function variantsOf(button) {
  try {
    return JSON.parse(button.dataset.variants || "[]");
  } catch (error) {
    return [];
  }
}

function limitsOf(button) {
  return (button.dataset.limits || "")
    .split(",")
    .map((value) => parseFloat(value.trim().replace(/\./g, "").replace(",", ".")))
    .filter((value) => !Number.isNaN(value))
    .sort((a, b) => a - b);
}

// On /cart the button carries the price of the cart's sofa this cover belongs to; on a product page it is the set's.
const inCart = (button) => button.hasAttribute("data-sofa-price");
const editing = (button) => button.closest("x-drawer")?.getAttribute("data-upsell-source") === "cart-edit";

function pick(button, config) {
  const variants = variantsOf(button);
  const sofaCents = parseInt(inCart(button) ? button.dataset.sofaPrice : config && config.getAttribute("data-bf-sofa-price"), 10);
  if (!variants.length || Number.isNaN(sofaCents)) return null;
  const euros = sofaCents / 100;
  const index = limitsOf(button).filter((limit) => euros >= limit).length;
  return variants[Math.min(index, variants.length - 1)];
}

// In the shop's money format (CH: "{{amount}} SFr."), not with a fixed euro sign: this store sells in francs.
function money(cents) {
  const value = cents / 100;
  const digits = Number.isInteger(value) ? 0 : 2;
  const format = window.themeVariables?.settings?.moneyFormat || "{{amount}}";
  const locale = /comma_separator/.test(format) ? "de-DE" : /apostrophe/.test(format) ? "de-CH" : "en-US";
  const amount = value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

// [price] in a text is replaced by the voucher's price; without it the price goes at the end.
const withPrice = (text, variant) =>
  (text || "").includes("[price]") ? text.split("[price]").join(money(variant.p)) : `${text || ""} ${money(variant.p)}`.trim();
const labelFor = (button, variant) => withPrice(button.dataset.label, variant);

function refresh(button) {
  const config = configFor(button.dataset.coverProductId);
  const variant = (inCart(button) || config) && !editing(button) ? pick(button, config) : null;
  button.hidden = !variant;
  if (!variant) return;
  button.textContent = labelFor(button, variant);
  button.dataset.variantId = variant.id;
}

function apply(button, config, variant) {
  const key = config.getAttribute("data-bf-cover-key");
  if (!key) return;
  const rowText = withPrice(button.dataset.rowText, variant);

  // upsell.js adds what the slot inputs hold, so they carry the voucher from now on.
  document.querySelectorAll(`input[data-input-id="${key}"]`).forEach((input) => {
    input.value = variant.id;
  });

  document.querySelectorAll(`input[data-upsell-product-key="${key}"]`).forEach((checkbox) => {
    const container = checkbox.closest("[data-upsell-matcher]");
    if (container) {
      container.dataset.currentVariantId = variant.id;
      container.dataset.currentVariantPrice = variant.p;
      container.dataset.currentVariantCompareAtPrice = "";
    }
    if (!checkbox.checked) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  config.setAttribute(ACTIVE, variant.id);
  document.dispatchEvent(
    new CustomEvent("upsell:variant-changed", {
      bubbles: true,
      detail: {
        referenceCheckboxId: key,
        variantId: variant.id,
        price: variant.p,
        compareAtPrice: null,
        variantTitle: rowText,
        variantOptions: rowText,
        imageSrc: button.dataset.image || "",
        kcCoverVoucher: true,
      },
    })
  );
}

document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-kc-cover-voucher]");
  if (!button) return;
  event.preventDefault();

  const config = configFor(button.dataset.coverProductId);
  const variant = inCart(button) || config ? pick(button, config) : null;
  if (!variant) return;
  if (inCart(button)) {
    // /cart: added like any offer from "Zu deiner Bestellung hinzufügen" (assets/cart.js _onAddVariant).
    document.dispatchEvent(
      new CustomEvent("cart:add-variant", { detail: { variantId: variant.id, properties: { [PROPERTY]: "1" } } })
    );
  } else {
    apply(button, config, variant);
  }

  const close = button.closest("x-drawer")?.querySelector('button[is="close-button"]');
  if (close) close.click();
});

// A colour saved in the modal puts the cover back.
document.addEventListener("upsell:variant-changed", (event) => {
  const detail = event.detail || {};
  if (detail.kcCoverVoucher || !detail.referenceCheckboxId) return;
  document.querySelectorAll(`[data-bf-config][data-bf-cover-key="${detail.referenceCheckboxId}"]`).forEach((config) => {
    config.removeAttribute(ACTIVE);
  });
});

// Another sofa (and so maybe another price bracket): new amount on the button, and a chosen voucher follows.
document.addEventListener("variant:change", () => {
  setTimeout(() => {
    buttons().forEach((button) => {
      refresh(button);
      const config = configFor(button.dataset.coverProductId);
      if (!config || !config.hasAttribute(ACTIVE)) return;
      const variant = pick(button, config);
      if (variant && String(variant.id) !== config.getAttribute(ACTIVE)) apply(button, config, variant);
    });
  }, 0);
});

// Opening the modal: the amount is read again then, whatever changed the sofa's price. On /cart the
// modal's mode (adding an offer or editing a line) is set by cart.js in the same click, hence the delay.
document.addEventListener("click", (event) => {
  if (event.target.closest("[data-bf-open-modal], [data-offer-picker], [data-edit-variant]")) {
    setTimeout(() => buttons().forEach(refresh), 0);
  }
}, true);

// The voucher line is marked, so the cart offers no "Bearbeiten" for it.
document.addEventListener("upsell:collect-items", (event) => {
  const items = event.detail && event.detail.items;
  if (!Array.isArray(items)) return;
  const active = new Set(
    [...document.querySelectorAll(`[data-bf-config][${ACTIVE}]`)].map((config) => String(config.getAttribute(ACTIVE)))
  );
  items.forEach((item) => {
    if (item && active.has(String(item.id))) item.properties = { ...(item.properties || {}), [PROPERTY]: "1" };
  });
});

buttons().forEach(refresh);
